import { Column } from "@/components/Column";
import { EmptyDataDash } from "@/components/EmptyDataDash";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { TableCell } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import {
  CommissionLedgerEntryType,
  EarningsLedgerEntry,
  EarningsPlacement,
} from "@/types";

// Commission column: current amount, with the full ledger history on hover so
// staffers can trace every re-tier, clawback, and manual tweak behind it.
export const EarningsTrackerCommissionCell = ({
  isDisabled,
  previousValue = null,
  placement,
  value,
}: {
  isDisabled: boolean;
  previousValue?: number | null;
  placement: EarningsPlacement;
  value: number | null;
}) => {
  const { userNameMap } = useTaggableUsers();

  /* Helpers */
  const getEntryLabel = (entry: EarningsLedgerEntry): string => {
    if (entry.type === CommissionLedgerEntryType.PAID) {
      return "Paid out";
    }
    if (entry.type === CommissionLedgerEntryType.CLAWBACK) {
      return "Clawed back";
    }
    if (entry.triggeredByCandidateName) {
      return `Re-tiered — ${entry.triggeredByCandidateName}'s placement moved the ladder`;
    }
    return `Manual adjustment by ${userNameMap[entry.createdByUserId]}`;
  };

  /* Render */
  const renderHistory = () => {
    let runningTotal = 0;

    return (
      <Column className="gap-2 py-1">
        <Text className="text-xs font-semibold text-slate-900">
          Commission history
        </Text>
        {placement.ledgerEntries.map((entry) => {
          runningTotal += entry.amount;

          return (
            <Column key={entry.id} className="gap-0.5">
              <Row className="items-start justify-between gap-6">
                <Column className="min-w-0 gap-0.5">
                  <Text className="text-xs font-medium text-slate-900">
                    {getEntryLabel(entry)}
                  </Text>
                  <Text className="text-xs font-normal text-slate-500">
                    {dates.formatIsoDateForUser(entry.effectiveDate)}
                    {entry.reason ? ` · ${entry.reason}` : ""}
                  </Text>
                </Column>
                <Row className="shrink-0 items-center gap-2">
                  <Text
                    className={cn(
                      "text-xs font-medium tabular-nums",
                      entry.amount < 0 ? "text-red-600" : "text-green-700",
                    )}
                  >
                    {entry.amount < 0 ? "−" : "+"}
                    {formatting.currency(Math.abs(entry.amount))}
                  </Text>
                  <Text className="w-16 text-right text-xs font-normal tabular-nums text-slate-500">
                    {formatting.currency(runningTotal)}
                  </Text>
                </Row>
              </Row>
            </Column>
          );
        })}
      </Column>
    );
  };

  const renderAmount = () => {
    if (previousValue !== null && value !== null) {
      return (
        <Row className="w-fit items-center gap-2">
          <Text className="text-sm font-medium tabular-nums text-slate-400 line-through">
            {formatting.currency(previousValue)}
          </Text>
          <Text className="text-sm font-medium tabular-nums text-green-700">
            {formatting.currency(value)}
          </Text>
        </Row>
      );
    }

    return (
      <Text
        className={cn(
          "w-fit text-sm font-medium tabular-nums",
          isDisabled ? "text-slate-400" : "text-green-700",
        )}
      >
        {formatting.currency(value)}
      </Text>
    );
  };

  const render = () => {
    if (value === null) {
      return (
        <TableCell>
          <EmptyDataDash />
        </TableCell>
      );
    }

    if (placement.ledgerEntries.length === 0) {
      return <TableCell>{renderAmount()}</TableCell>;
    }

    return (
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <Row className="w-fit cursor-default">{renderAmount()}</Row>
          </TooltipTrigger>
          <TooltipContent align="start" className="max-w-sm">
            {renderHistory()}
          </TooltipContent>
        </Tooltip>
      </TableCell>
    );
  };

  return render();
};
